"use client"

// /work-with-me — client sections (V3-MASTER-SPEC §2.8; copy deck v2 §6).
// Register: PAPER — an offer ladder is a document you read, not an instrument.
// ZERO prices on site (pricing policy LOCKED 2026-07-08).

import Link from "next/link"
import { CtaBlock } from "@/components/v3/site-chrome"
import { Eyebrow, FadeUp, ZH_SERIF, paper } from "@/components/v3/primitives"
import { workWithMe } from "@/lib/proto/content"

export function OpeningSection() {
  return (
    <section aria-label="合作" className="relative mx-auto max-w-6xl px-6 pb-16 pt-28 md:px-10 md:pb-24 md:pt-36">
      <FadeUp>
        <Eyebrow zh="合作" en="Work with me" />
        <h1
          lang="zh-Hant"
          className="mt-8 max-w-4xl text-balance text-4xl leading-[1.25] md:text-[3.8rem] md:leading-[1.2]"
          style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 620", color: paper.ink }}
        >
          不是每個問題，都需要委任。
        </h1>
      </FadeUp>
      <FadeUp delay={0.08}>
        <p
          lang="zh-Hant"
          className="mt-8 max-w-3xl text-lg leading-[1.95] md:text-xl"
          style={{ fontFamily: "var(--proto-hanken)", color: paper.inkSoft }}
        >
          {workWithMe.opening.zh}
        </p>
        <p
          className="mt-5 max-w-3xl pl-5 text-[13px] leading-relaxed"
          style={{
            fontFamily: "var(--proto-hanken)",
            borderLeft: `1px solid ${paper.hairline}`,
            color: paper.inkFaint,
          }}
        >
          {workWithMe.opening.en}
        </p>
      </FadeUp>
    </section>
  )
}

export function LadderSection() {
  return (
    <section
      aria-label="合作方式"
      className="relative py-24 md:py-32"
      style={{
        background: paper.bgDeep,
        borderTop: `1px solid ${paper.hairlineSoft}`,
        borderBottom: `1px solid ${paper.hairlineSoft}`,
      }}
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <ol className="space-y-6">
          {workWithMe.offers.map((offer, i) => (
            <FadeUp as="li" key={offer.id} delay={i * 0.04}>
              <article
                className="group relative rounded-2xl p-8 transition-colors duration-500 md:p-10"
                style={{
                  background: paper.bg,
                  border: offer.flagship ? `1.5px solid ${paper.accent}` : `1px solid ${paper.hairline}`,
                }}
              >
                {offer.flagship && (
                  <span
                    className="absolute right-6 top-6 text-[10px] tracking-[0.24em]"
                    style={{ fontFamily: "var(--proto-mono)", color: paper.accent }}
                    aria-hidden
                  >
                    FLAGSHIP
                  </span>
                )}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-[3rem_minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-8">
                  <span className="text-sm" style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}>
                    0{i + 1}
                  </span>
                  <div>
                    <h2
                      lang="zh-Hant"
                      className="text-xl md:text-[1.6rem] md:leading-[1.4]"
                      style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 600", color: paper.ink }}
                    >
                      {offer.name.zh}
                    </h2>
                    <p
                      lang="zh-Hant"
                      className="mt-2 text-[12px] tracking-[0.12em]"
                      style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}
                    >
                      {offer.tier.zh}
                    </p>
                  </div>
                  <div>
                    <p
                      lang="zh-Hant"
                      className="text-[15px] leading-[1.95]"
                      style={{ fontFamily: "var(--proto-hanken)", color: paper.inkSoft }}
                    >
                      {offer.line.zh}
                    </p>
                    <p
                      className="mt-3 text-[12.5px] leading-relaxed"
                      style={{ fontFamily: "var(--proto-hanken)", color: paper.inkFaint }}
                    >
                      {offer.line.en}
                    </p>
                    {offer.id === "diagnostic" && (
                      <Link
                        href="/brand-engineering#diagnostic"
                        lang="zh-Hant"
                        className="mt-4 inline-flex items-center gap-2 text-[13px] transition-colors duration-300"
                        style={{ fontFamily: "var(--proto-mono)", color: paper.accent }}
                      >
                        先試 90 秒診斷 →
                      </Link>
                    )}
                  </div>
                </div>
              </article>
            </FadeUp>
          ))}
        </ol>

        {/* 定價政策 — LOCKED: 全站零價格 */}
        <FadeUp delay={0.2}>
          <p
            lang="zh-Hant"
            className="mt-12 text-center text-[13px] leading-relaxed"
            style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}
          >
            {workWithMe.pricingNote.zh}
          </p>
        </FadeUp>
      </div>
    </section>
  )
}

// 收尾 — RedPeak 交付 (LOCKED) + 連續性段落連結 (§4.5)
export function DeliverySection() {
  return (
    <section aria-label="交付" className="relative mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-28">
      <FadeUp>
        <div className="mx-auto max-w-3xl text-center">
          <p
            lang="zh-Hant"
            className="text-balance text-xl leading-[1.7] md:text-2xl"
            style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 540", color: paper.ink }}
          >
            {workWithMe.closing.zh}
          </p>
          <p
            className="mt-3 text-[13px] italic"
            style={{ fontFamily: "var(--proto-fraunces)", color: paper.inkFaint }}
          >
            {workWithMe.closing.en}
          </p>
        </div>
      </FadeUp>
    </section>
  )
}

export function WorkWithMeCta() {
  return <CtaBlock />
}
